import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { truthy } from '@/util/array';

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetSnapshotListByUniqueId = graphql(/* GraphQL */ `
  query AppliedSnapshots(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $uniqueIds: [String!]
    $userId: BigInt
  ) {
    environment(id: $environmentId, userId: $userId) {
      adapterType
      applied {
        lastUpdatedAt
        snapshots(first: $first, after: $after, filter: { uniqueIds: $uniqueIds }) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              tags
              description
              database
              schema
              rawCode
              meta
              alias
              compiledCode
              executionInfo {
                lastRunId
                lastRunStatus
                lastSuccessRunId
                executeCompletedAt
              }
              catalog {
                owner
                type
                columns {
                  type
                  name
                  index
                  comment
                  tags
                }
                rowCountStat
                bytesStat
                stats {
                  id
                  label
                  value
                  description
                  include
                }
              }
            }
          }
        }
      }
    }
  }
`);

type SnapshotByEnvironmentParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  uniqueIds?: string[];
  userId: number | 'loading' | null;
  pause?: boolean;
};

export const useDiscoveryAppliedSnapshots = ({
  first,
  after,
  environmentId,
  uniqueIds,
  userId,
  pause,
}: SnapshotByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetSnapshotListByUniqueId,
      variables: {
        first,
        after,
        environmentId,
        uniqueIds,
        userId: userId as number | null,
      },
      pause: pause || !environmentId || uniqueIds?.length === 0,
    },
    (data) => {
      const snapshots = data.environment.applied?.snapshots;
      const pageInfo = snapshots?.pageInfo;
      if (snapshots == null || pageInfo == null) return undefined;
      return {
        totalCount: snapshots.totalCount ?? null,
        endCursor: snapshots.pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
        metadata: { adapterType: data.environment.adapterType },
      };
    },
    (data) => {
      return (data.environment.applied?.snapshots.edges || [])
        .map((edge) => edge.node)
        .filter(truthy);
    },
    (data) => data.environment.applied?.lastUpdatedAt,
  );

  useCacheMonitor({ applied: query.lastUpdatedAt });

  return query;
};
